/**
 * The transport a hosted game uses: postMessage to the trusted parent frame.
 *
 * This is the default and the one the platform is designed around. The game
 * holds no credential it could use directly — it cannot reach the API, because
 * its CSP forbids it — so the shell performs every privileged call on its behalf
 * and hands back the result.
 *
 * The shell is trusted here in a narrow and specific sense: it is trusted to
 * relay, not to decide. It cannot change the ranking track (a database trigger
 * owns that column), it cannot alter a score value without the proof failing,
 * and it never sees a provider payload.
 */
import { SDK_CHANNEL, parseShellMessage, } from './bridge-protocol.js';
import { bootstrapPort } from './external-shell-bridge.js';
import { splitSessionToken } from './direct-transport.js';
import { TRYPORTALError } from './errors.js';
import { newUuid } from './ids.js';
import { isGameActionEnvelope } from './validate.js';
export class BridgeTransport {
    kind = 'BRIDGE';
    session;
    sessionNonce;
    bootstrap;
    win;
    requestTimeoutMs;
    scoreWaiters = new Map();
    shareWaiters = new Map();
    leaderboardWaiters = new Map();
    streamModeWaiters = new Map();
    creatorVoiceWaiters = new Map();
    handlers;
    listener;
    closed = false;
    port;
    constructor(options) {
        this.bootstrap = options.bootstrap;
        this.port = bootstrapPort(options.bootstrap);
        this.win = options.window;
        this.requestTimeoutMs = options.requestTimeoutMs ?? 15_000;
        this.sessionNonce = splitSessionToken(options.bootstrap.sessionToken).nonce;
        this.session = {
            gameSessionId: options.bootstrap.gameSessionId,
            rankingTrack: normaliseTrack(options.bootstrap.rankingTrack),
            competitionId: options.bootstrap.competitionId,
            liveSessionId: options.bootstrap.liveSessionId ?? null,
        };
    }
    post(message) {
        if (this.port !== undefined) {
            if (!this.closed)
                this.port.postMessage(message);
            return;
        }
        if (!this.closed)
            this.win.parent.postMessage(message, this.bootstrap.expectedParentOrigin);
    }
    start(handlers) {
        this.handlers = handlers;
        const listener = (event) => {
            // Same reasoning as `bootstrap.ts`: the frame's origin is opaque, so the
            // shell posts with a real target origin but the game cannot compare
            // `event.origin` against anything meaningful. Window identity can be
            // compared, and it is the property an attacker cannot forge.
            //
            // This matters most for ACTION frames. Everything the platform guarantees
            // about a viewer's comment — that it was moderated, that its TTL is
            // honoured, that it is not a replay — is a guarantee about a message that
            // came from the shell. A message from anywhere else carries none of it
            // while looking identical.
            if (event.source !== this.win.parent)
                return;
            void this.handle(event.data);
        };
        if (this.port !== undefined) {
            this.port.onmessage = (event) => {
                if (!this.closed)
                    void this.handle(event.data);
            };
            this.port.start();
        }
        else {
            this.listener = listener;
            this.win.addEventListener('message', listener);
        }
        handlers.onStateChange('CONNECTED');
        this.post({ channel: SDK_CHANNEL, type: 'READY', sdkVersion: this.bootstrap.sdkVersion });
        // The bootstrap already arrived, so there is nothing left to wait for. The
        // promise exists to keep the two transports interchangeable.
        return Promise.resolve();
    }
    async handle(data) {
        const message = parseShellMessage(data);
        if (message === undefined)
            return;
        const handlers = this.handlers;
        if (handlers === undefined)
            return;
        switch (message.type) {
            case 'ACTION': {
                // Step 1 of the required algorithm. The shell relayed this without
                // inspecting it, so the game validates it here exactly as it would a
                // frame off a socket.
                if (!isGameActionEnvelope(message.envelope)) {
                    handlers.log('discarded a relayed action that failed contract validation');
                    return;
                }
                if (message.envelope.gameSessionId !== this.session.gameSessionId) {
                    handlers.log('discarded a relayed action addressed to another session');
                    return;
                }
                const ack = await handlers.onAction(message.envelope);
                this.post({ channel: SDK_CHANNEL, type: 'ACTION_ACK', ack });
                return;
            }
            case 'SESSION_STATE':
                handlers.onSessionState(message.state, message.killSwitchActive, message.reason ?? 'session state changed');
                return;
            case 'SCORE_RESULT': {
                const waiter = this.scoreWaiters.get(message.requestId);
                if (waiter === undefined)
                    return;
                this.scoreWaiters.delete(message.requestId);
                clearTimeout(waiter.timer);
                if (message.ok && message.receipt !== undefined) {
                    waiter.resolve(message.receipt);
                }
                else {
                    waiter.reject(new TRYPORTALError(message.retryable === true ? 'SCORE_UNREACHABLE' : 'SCORE_REJECTED', message.message ?? 'the platform did not accept this score', { retryable: message.retryable === true }));
                }
                return;
            }
            case 'SHARE_CARD_RESULT': {
                const waiter = this.shareWaiters.get(message.requestId);
                if (waiter === undefined)
                    return;
                this.shareWaiters.delete(message.requestId);
                clearTimeout(waiter.timer);
                if (message.ok && message.card !== undefined) {
                    waiter.resolve(message.card);
                }
                else {
                    waiter.reject(new TRYPORTALError('SHARE_CARD_FAILED', message.message ?? 'the platform did not create a share card'));
                }
                return;
            }
            case 'LEADERBOARD_RESULT': {
                const waiter = this.leaderboardWaiters.get(message.requestId);
                if (waiter === undefined)
                    return;
                this.leaderboardWaiters.delete(message.requestId);
                clearTimeout(waiter.timer);
                if (message.ok && message.page !== undefined) {
                    // Resolved even when `page.myEntry` is null. That is a guest's normal
                    // board (invariant 4), not an error.
                    waiter.resolve(message.page);
                }
                else {
                    waiter.reject(new TRYPORTALError('LEADERBOARD_UNAVAILABLE', message.message ?? 'the platform did not return a leaderboard page', { retryable: true }));
                }
                return;
            }
            case 'STREAM_MODE_RESULT': {
                const waiter = this.streamModeWaiters.get(message.requestId);
                if (waiter === undefined)
                    return;
                this.streamModeWaiters.delete(message.requestId);
                clearTimeout(waiter.timer);
                waiter.resolve({
                    ok: message.ok,
                    ...(message.message === undefined ? {} : { message: message.message }),
                });
                return;
            }
            case 'CREATOR_VOICE_RESULT': {
                const waiter = this.creatorVoiceWaiters.get(message.requestId);
                if (waiter === undefined)
                    return;
                this.creatorVoiceWaiters.delete(message.requestId);
                clearTimeout(waiter.timer);
                waiter.resolve({
                    ok: message.ok,
                    submitted: message.submitted,
                    ...(message.message === undefined ? {} : { message: message.message }),
                });
                return;
            }
            case 'BOOTSTRAP':
                // Delivered once before this transport exists, and handled by
                // `waitForBootstrap`. A second one would mean the frame re-navigated,
                // which the shell does not do; ignoring it is safer than re-binding a
                // session mid-run.
                return;
            default:
                return;
        }
    }
    request(waiters, requestId, send, timeoutCode, timeoutMs = this.requestTimeoutMs) {
        if (this.closed) {
            return Promise.reject(new TRYPORTALError('SDK_CLOSED', 'the SDK has already been closed'));
        }
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                waiters.delete(requestId);
                reject(new TRYPORTALError(timeoutCode, `the play shell did not answer within ${String(timeoutMs)}ms`, {
                    retryable: timeoutCode === 'SCORE_UNREACHABLE' || timeoutCode === 'LEADERBOARD_UNAVAILABLE',
                }));
            }, timeoutMs);
            // Not `unref`ed: see bootstrap.ts. A timeout that does not fire is not a
            // timeout, and this one is cleared on every settled path.
            waiters.set(requestId, { resolve, reject, timer });
            send();
        });
    }
    submitScore(request) {
        const requestId = newUuid();
        return this.request(this.scoreWaiters, requestId, () => {
            this.post({ channel: SDK_CHANNEL, type: 'SUBMIT_SCORE', requestId, request });
        }, 'SCORE_UNREACHABLE');
    }
    createShareCard(input) {
        const requestId = newUuid();
        return this.request(this.shareWaiters, requestId, () => {
            this.post({
                channel: SDK_CHANNEL,
                type: 'CREATE_SHARE_CARD',
                requestId,
                scoreSubmissionId: input.scoreSubmissionId,
                ...(input.message === undefined ? {} : { message: input.message }),
            });
        }, 'SHARE_CARD_FAILED');
    }
    /**
     * Ask the shell for a page of this session's board.
     *
     * Nothing identifying the board travels: see `GET_LEADERBOARD` in
     * `bridge-protocol.ts`. The query is a window into a board the shell already
     * knows the identity of.
     */
    leaderboard(query) {
        const requestId = newUuid();
        return this.request(this.leaderboardWaiters, requestId, () => {
            this.post({
                channel: SDK_CHANNEL,
                type: 'GET_LEADERBOARD',
                requestId,
                ...(query.limit === undefined ? {} : { limit: query.limit }),
                ...(query.cursor === undefined ? {} : { cursor: query.cursor }),
            });
        }, 'LEADERBOARD_UNAVAILABLE');
    }
    /**
     * Passkey, TikTok handle, and confirmation all happen in the shell. Ten minutes
     * is longer than a score relay because a human is filling a form, not a
     * network hop.
     */
    requestStreamMode(desired) {
        const requestId = newUuid();
        return this.request(this.streamModeWaiters, requestId, () => {
            this.post({
                channel: SDK_CHANNEL,
                type: 'STREAM_MODE_REQUEST',
                requestId,
                desired,
            });
        }, 'STREAM_MODE_FAILED', 10 * 60 * 1000);
    }
    openStreamSettings() {
        if (this.closed) {
            return Promise.reject(new TRYPORTALError('SDK_CLOSED', 'the SDK has already been closed'));
        }
        this.post({
            channel: SDK_CHANNEL,
            type: 'STREAM_SETTINGS_OPEN',
            requestId: newUuid(),
        });
        return Promise.resolve();
    }
    openCreatorVoice() {
        const requestId = newUuid();
        return this.request(this.creatorVoiceWaiters, requestId, () => {
            this.post({
                channel: SDK_CHANNEL,
                type: 'CREATOR_VOICE_OPEN',
                requestId,
            });
        }, 'CREATOR_VOICE_FAILED', 10 * 60 * 1000);
    }
    reportGameOver(reason) {
        if (this.closed)
            return;
        this.post({ channel: SDK_CHANNEL, type: 'GAME_OVER', reason });
    }
    close(reason) {
        if (this.closed)
            return;
        this.post({ channel: SDK_CHANNEL, type: 'GAME_OVER', reason });
        this.closed = true;
        if (this.port !== undefined) {
            this.port.onmessage = null;
            this.port.close();
        }
        if (this.listener !== undefined) {
            this.win.removeEventListener('message', this.listener);
            this.listener = undefined;
        }
        // Every waiter is answered before the listener goes away. A promise left
        // pending after close() is a game that hangs on a screen it can never leave.
        abandon(this.scoreWaiters, 'the SDK closed while a score was in flight');
        abandon(this.shareWaiters, 'the SDK closed while a request was in flight');
        abandon(this.leaderboardWaiters, 'the SDK closed while a leaderboard read was in flight');
        abandon(this.streamModeWaiters, 'the SDK closed while a stream-mode request was in flight');
        abandon(this.creatorVoiceWaiters, 'the SDK closed while a creator-voice request was in flight');
        this.handlers?.onStateChange('CLOSED');
    }
}
/** Reject and clear every waiter in one map. */
function abandon(waiters, reason) {
    for (const [, waiter] of waiters) {
        clearTimeout(waiter.timer);
        waiter.reject(new TRYPORTALError('SDK_CLOSED', reason));
    }
    waiters.clear();
}
function normaliseTrack(value) {
    return value === 'LIVE_INTERACTIVE' || value === 'OFFICIAL_EVENT' ? value : 'STANDARD';
}
